import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Clock } from "lucide-react";

const Shell = ({ children, testId }) => (
  <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
    <div className="rle-card p-8 sm:p-10 max-w-lg w-full text-center" data-testid={testId}>{children}</div>
  </div>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ phase: "checking", tx: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ phase: "error" }); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState({ phase: "paid", tx: data }); refresh?.(); return; }
        if (data.payment_status === "expired" || data.status === "failed") { setState({ phase: "failed", tx: data }); return; }
      } catch { /* keep polling */ }
      if (attempts.current >= 15) { setState({ phase: "timeout" }); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refresh]);

  if (state.phase === "checking") return (
    <Shell testId="payment-checking">
      <Loader2 size={32} className="mx-auto text-emerald-700 animate-spin"/>
      <h1 className="mt-4 font-display font-bold text-2xl text-slate-900">Confirming your payment…</h1>
      <p className="text-slate-600 mt-2">This usually takes a few seconds. Please don't close this page.</p>
    </Shell>
  );
  if (state.phase === "paid") return (
    <Shell testId="payment-success">
      <CheckCircle2 size={40} className="mx-auto text-emerald-600"/>
      <span className="rle-eyebrow block mt-4">Payment confirmed</span>
      <h1 className="mt-1 font-display font-extrabold text-3xl text-slate-900">Welcome to {state.tx.plan[0].toUpperCase() + state.tx.plan.slice(1)}</h1>
      <p className="text-slate-600 mt-3">Your {state.tx.period} subscription is active and renews automatically. AI missions, detailed feedback, speaking practice and daily drills are unlocked.</p>
      <p className="text-xs text-slate-500 mt-2" data-testid="payment-success-amount">Paid {state.tx.currency.toUpperCase()} {Number(state.tx.amount).toFixed(2)}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <Link to="/missions"><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="payment-success-missions-btn">Start a mission</Button></Link>
        <Link to="/dashboard"><Button variant="outline" className="rounded-full" data-testid="payment-success-dashboard-btn">Go to dashboard</Button></Link>
      </div>
    </Shell>
  );
  if (state.phase === "timeout") return (
    <Shell testId="payment-timeout">
      <Clock size={36} className="mx-auto text-amber-600"/>
      <h1 className="mt-4 font-display font-bold text-2xl text-slate-900">Still confirming</h1>
      <p className="text-slate-600 mt-2">Your payment is being processed. Your plan will update automatically within a few minutes — check your Profile.</p>
      <Link to="/profile"><Button variant="outline" className="rounded-full mt-6">Open Profile</Button></Link>
    </Shell>
  );
  return (
    <Shell testId="payment-failed">
      <XCircle size={36} className="mx-auto text-red-500"/>
      <h1 className="mt-4 font-display font-bold text-2xl text-slate-900">Payment not completed</h1>
      <p className="text-slate-600 mt-2">No charge was made. You can try again whenever you're ready.</p>
      <Link to="/pricing"><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 mt-6" data-testid="payment-failed-retry-btn">Back to pricing</Button></Link>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testId="payment-cancel">
      <XCircle size={36} className="mx-auto text-slate-400"/>
      <h1 className="mt-4 font-display font-bold text-2xl text-slate-900">Checkout cancelled</h1>
      <p className="text-slate-600 mt-2">No payment was taken. Your free Explore plan is still active.</p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <Link to="/pricing"><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="payment-cancel-pricing-btn">See plans</Button></Link>
        <Link to="/dashboard"><Button variant="outline" className="rounded-full">Back to dashboard</Button></Link>
      </div>
    </Shell>
  );
}
